import { useAuth, useMagentoState } from '@checkout/composables';
import { useAsyncMethods } from '@checkout/composables/core';
import type { UseCart } from '@checkout/composables/useCart/useCart.model';
import { CartService } from '@checkout/services';
import { useCartStore } from '@checkout/stores';
import { useContext } from '@nuxtjs/composition-api';
import merge from 'lodash.merge';
import cloneDeep from 'lodash/cloneDeep';
import { storeToRefs } from 'pinia';

import type { Cart } from '~/models';
import { useCartStore as useOldCartStore } from '~/modules/core/stores/cart';
import type { Cart as OldCart } from '~/modules/GraphQL/types';

export function useCart(): UseCart {
  const { app } = useContext();
  const { isAuthenticated } = useAuth();
  const cartStore = useCartStore();
  const oldCartStore = useOldCartStore();
  const { cart } = storeToRefs(cartStore);

  const magentoState = useMagentoState();
  const cartService = new CartService(app);

  const deepRemoveFromBaseCart = (baseObj: Cart, partObj: Partial<Cart>) => {
    for (const key in partObj) {
      if (!Object.prototype.hasOwnProperty.call(partObj, key)) continue;

      const partValue = partObj[key];
      const baseValue = baseObj[key];

      if (partValue !== null && typeof partValue === 'object') {
        if (Array.isArray(partValue)) {
          if (Array.isArray(baseValue)) {
            baseObj[key] = baseValue.filter((item, index) => JSON.stringify(item) !== JSON.stringify(partValue[index]));
          }
        } else if (baseValue && typeof baseValue === 'object') {
          deepRemoveFromBaseCart(baseValue, partValue);
        }
      } else {
        if (baseValue === partValue) {
          delete baseObj[key];
        }
      }
    }
  };

  const removeAvailableShippingMethods = (baseCart: Cart, cartPart: Partial<Cart>) => {
    if (!cartPart?.shipping_addresses) return;

    const hasPartWithMethods = cartPart.shipping_addresses.some((addr) => addr?.available_shipping_methods);

    if (hasPartWithMethods && baseCart.shipping_addresses) {
      baseCart.shipping_addresses.forEach((addr) => {
        addr.available_shipping_methods = [];
      });
    }
  };

  /**
   * Updates the cart partially, with specific parts to replace instead of merge
   * @param cartPart - Partial cart data to update
   * @param replaceParts - Array of keys that should be replaced rather than merged
   */
  const updateCartPartially = (cartPart: Partial<Cart>, replaceParts: (keyof Cart)[] = []) => {
    const baseCart: Cart = cloneDeep(cart.value);

    deepRemoveFromBaseCart(baseCart, cartPart);
    removeAvailableShippingMethods(baseCart, cartPart);

    const updatedCart: Cart = merge(baseCart, cartPart);

    replaceParts.forEach((key: string) => {
      if (Object.prototype.hasOwnProperty.call(cartPart, key)) {
        updatedCart[key] = cartPart[key];
      }
    });

    cartStore.setCart(updatedCart);
  };

  const { methods, errors, loading } = useAsyncMethods({
    composableName: 'useCart',
    methodsFactory: () => ({
      mergeCarts: async () => {
        const currentCartId: string = magentoState.getCartId();
        const {
          customerCart: { id }
        } = await cartService.getUserCartId();

        if (id && currentCartId && currentCartId !== id) {
          cartStore.setMergeCartsFlag(true);

          const { mergeCarts } = await cartService.mergeCarts({ sourceCartId: currentCartId, destinationCartId: id });
          if (errors.mergeCarts.value) {
            cartStore.resetCart();
            cartStore.setMergeCartsFlag(false);
            return;
          }

          cartStore.setCart(mergeCarts);
          magentoState.setCartId(mergeCarts.id);
          cartStore.setMergeCartsFlag(false);

          oldCartStore.$patch((state) => {
            state.cart = mergeCarts as unknown as OldCart;
          });
        } else {
          cartStore.resetCart();
          oldCartStore.$patch((state) => {
            state.cart = null;
          });
        }
      },
      createCart: async () => {
        const { createEmptyCart: cartId } = await cartService.createEmptyCart();
        magentoState.setCartId(cartId);

        return cartId;
      },
      loadCart: async () => {
        cartStore.setCartLoadingFlag(true);

        if (isAuthenticated.value) {
          const { customerCart } = await cartService.getCustomerCart();
          magentoState.setCartId(customerCart.id);
          cartStore.setCart(customerCart);
        } else {
          let cartId = magentoState.getCartId();
          if (!cartId) {
            cartId = await methods.createCart();
          }

          const { cart } = await cartService.getCart(cartId);
          magentoState.setCartId(cartId);
          cartStore.setCart(cart);
        }

        cartStore.setCartLoadingFlag(false);
      }
    })
  });

  return {
    cart,
    ...methods,
    errors,
    loading,
    clearCart: () => cartStore.resetCart(),
    updateCartPartially
  };
}
